import { Injectable } from '@angular/core';
import { Router, } from '@angular/router';
import { Actions, createEffect, ofType, } from '@ngrx/effects';
import { ROUTER_NAVIGATION, RouterNavigationAction, } from '@ngrx/router-store';
import { select, Store } from '@ngrx/store';
import { from, Observable, of, } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  pairwise,
  pluck,
  startWith,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  APPLY_RESOURCE_FACETS,
  ApplyResourceFacetsAction,
  CLEAR_FACETS_LOCK_IN_STORAGE,
  ClearResourceFacetsAction,
  CollapseResourceFacetsAction,
  ExpandResourceFacetsAction,
  FacetActions,
  HANDLE_FACETS_LOCK_IN_STORAGE,
  LazyFacetLoadAction,
  LOAD_LAZY_FACET,
  LOAD_RESOURCE_FACETS,
  LoadResourceFacetsAction,
  loadSingleLazyFacet,
  singleLazyFacetLoadCompleteAction,
  REMOVE_FACET_BUBBLE,
  RESET_FACET_BUBBLES,
  ResourceFacetsLoadCompleteAction,
  ToggleFacetBubbleLockAction,
  UPDATE_SEARCH_OBJECT,
  UpdateSearchObjectAction,
} from 'search/actions/facet.actions';
import { FacetSchemaProps } from 'search/facets/models/facet-schema';
import { FacetSchemaWithData } from 'search/facets/models/facet-schema-with-data';
import { FacetURLFilters } from 'search/facets/models/facet-state';
import { FacetType } from 'search/facets/models/facet-type';
import {
  AllResourceFacets,
  CatalogDateFacet,
  DateFacet,
  Facet,
  FacetResult,
  FacetsLocked,
  ResourceFacet
} from 'search/facets/models/resource-facet';
import { getProcessor } from 'search/facets/processors/actions/facets-actions-factory';
import { FacetsSchemaService } from 'search/facets/services/facets-schema.service';
import { DEFAULT_LAZY_LOADED_FACET_SEARCH_OBJECT, MappedSearchObject } from 'search/models/search-object';
import { MAX_PAGE_NUMBER, MAX_PAGE_SIZE, OAI_MATERIAL_TYPE_REGEX, } from 'search/models/search-results';
import { Advanced } from 'search/models/search-term';
import { SearchMapperService } from 'search/services/mappers/search-mapper.service';
import { areEqualDeeply } from 'shared/utils/are-equal-deeply';
import { resetBulkSelection } from '../../bulk-select/actions/bulk-select.actions';
import { BulkFeatureKey } from '../../bulk-select/enums/bulk-feature-key';
import { resetEntities } from '../../entity/actions/entity.actions';
import { resetSavedSearchRunState } from '../../saved-search/actions/saved-search.actions';
import { getRunResults } from '../../saved-search/reducers/saved-search.reducer';
import { FeatureToggleService } from '../../services/feature-toggle.service';
import {
  SEARCH_RESULTS_LOAD_COMPLETE,
  SearchActions,
  searchChanged,
  SearchResultsLoadAction,
  SearchResultsLoadCompleteAction,
  SearchResultsLoadCompleteActionPayload,
  SearchResultsPageRouteNavigation,
} from '../actions/search.actions';
import { FacetsService } from '../facets/services/facets.service';
import {
  getAdvancedMode,
  getAppliedFacetsArray,
  getCurrentSearchInput,
  getLastSearchResultsUrl,
  getSearchResultsMetadata,
  getV2Facets,
  State,
  getRapidoMode,
} from '../reducers/root.reducer';

const facetKeyStorage = 'FACETS_LOCKED';

@Injectable()
export class SearchFacetsEffects {

  private LAZY_LOADED_SINGLE_FACETS: Partial<FacetSchemaProps & {searchObject: Partial<MappedSearchObject>}>[] = [];

  public applyFacetsRoute$: Observable<any> = createEffect(() => this.actions$.pipe(
    ofType(ROUTER_NAVIGATION),
    map((action: RouterNavigationAction) => action.payload),
    withLatestFrom(
      this.store.pipe(select(getRunResults)),
    ),
    filter(([, runSavedSearchResponse]) => !runSavedSearchResponse),
    map(([routerState]) => {
      const url = routerState.event.url;
      if (!this.isValidSearchUrl(url)) {
        return null;
      }
      const queryParams = {...routerState.routerState.root.queryParams};
      const searchObject = this.searchMapperService.prepareNewSearchObjectFromQuery(queryParams,
        this.facetSchemaService.getActualFacetSchemas());

      return {
        queryParams,
        searchObject,
        url,
      };
    }),
    distinctUntilChanged((oldState, newState) => {
      if (!oldState || !newState) {
        return false;
      }
      return oldState.url === newState.url || areEqualDeeply(oldState.searchObject, newState.searchObject);
    }),
    filter(Boolean),
    switchMap(({queryParams, searchObject, url}) => {
      this.validateAndNavigate(queryParams);
      return from([
        new SearchResultsPageRouteNavigation(url),
        new UpdateSearchObjectAction(searchObject),
        new SearchResultsLoadAction(),
        searchChanged({url}),
      ]);
    }),
  ));

  public resetEntitiesStateOnUpdateSearchObject$ = createEffect(() => this.actions$.pipe(
    ofType(UPDATE_SEARCH_OBJECT),
    switchMap(() => [
      resetEntities(),
      resetBulkSelection({bulkFeatureKey: BulkFeatureKey.SRP}),
    ]),
  ));

  public applyFacetsRouteFromRunSavedSearch$: Observable<any> = createEffect(() => this.actions$.pipe(
    ofType(ROUTER_NAVIGATION),
    map((action: RouterNavigationAction) => action.payload),
    withLatestFrom(this.store.pipe(select(getRunResults))),
    filter(([, runSavedSearchResponse]) => !!runSavedSearchResponse),
    map(([routerState, { searchObject, results }]) => {
      const url = routerState.event.url;
      if (!this.isValidSearchUrl(url)) {
        return null;
      }
      const queryParams = {...routerState.routerState.root.queryParams};
      const mappedSearchObject = this.searchMapperService.prepareMappedSearchObjectFromSearchObject(searchObject);

      return {
        queryParams,
        url,
        searchObject,
        mappedSearchObject,
        results,
      };
    }),
    filter((data) => !!data),
    switchMap(({queryParams, url, searchObject, mappedSearchObject, results}) => {
      this.validateAndNavigate(queryParams);
      return from([
        new SearchResultsPageRouteNavigation(url),
        new UpdateSearchObjectAction(searchObject),
        new SearchResultsLoadCompleteAction({ searchObject: mappedSearchObject, results }),
        searchChanged({url}),
        resetSavedSearchRunState(),
      ]);
    }),
  ));

  public facetsReload: Observable<any> = createEffect(() => this.actions$.pipe(
    ofType(SEARCH_RESULTS_LOAD_COMPLETE),
    map((action: SearchResultsLoadCompleteAction) => action.payload),
    startWith<SearchResultsLoadCompleteActionPayload | null>(null),
    pairwise(),
    map(([oldSearch, newSearch]) => [oldSearch ? oldSearch.searchObject : null, newSearch.searchObject]),
    map(([oldSearchObject, newSearchObject]) => {
      const searchTextNotChanged = oldSearchObject &&
        ((oldSearchObject.metadataBoolQuery && newSearchObject.metadataBoolQuery
          && (oldSearchObject.metadataBoolQuery === newSearchObject.metadataBoolQuery))
          || (oldSearchObject.searchText && newSearchObject.searchText
            && (oldSearchObject.searchText === newSearchObject.searchText)));
      const isPageChanged = searchTextNotChanged && (newSearchObject.pageNum !== oldSearchObject.pageNum) &&
        (JSON.stringify(oldSearchObject.filters ? oldSearchObject.filters.facets : '') ===
          JSON.stringify(newSearchObject.filters ? newSearchObject.filters.facets : ''));
      return {newSearchObject, isPageChanged, searchTextNotChanged};
    }),
    filter(({isPageChanged}) => !isPageChanged),
    switchMap(({newSearchObject: searchObject, searchTextNotChanged}) => {
      searchObject.facetTypes = this.facetSchemaService.getInitialFacetTypes();
      if (searchObject.filters && searchObject.filters.facets && searchObject.filters.facets.agentIds) {
        searchObject.facetTypes.push('agent');
      }
      if (searchObject.filters && searchObject.filters.facets && searchObject.filters.facets.conceptIds) {
        searchObject.facetTypes.push('concept');
      }
      return of(
        new CollapseResourceFacetsAction(),
        new LoadResourceFacetsAction({searchObject, changedQuery: !searchTextNotChanged}),
      );
    }),
  ));

  public loadFacets: Observable<any> = createEffect(() => this.actions$.pipe(
    ofType(LOAD_RESOURCE_FACETS),
    switchMap((action: LoadResourceFacetsAction) => {
      const searchObject = action.payload.searchObject;
      return this.facetsService.getFacets(searchObject).pipe(
        withLatestFrom(this.store.pipe(select(getSearchResultsMetadata)), this.store.pipe(select(getAdvancedMode))),
        switchMap(([facets, rollupResults, advancedMode]) => {
          const srpQueryResults = rollupResults;
          let hasSelectedFacets = false;
          if (advancedMode) {
            hasSelectedFacets = this.lockAdvancedSearchFacets(
                {...searchObject.filters.facets} as FacetURLFilters,
                (facets as AllResourceFacets).newModel
            );
          }
          const facetsLocked: FacetsLocked = SearchFacetsEffects.getStoredLockedFacetsIds();
          const topFacets = this.facetSchemaService.getActualFacetSchemas().slice(0,3).map(facet => facet.toJS());
          const lazyLoadedFacets = this.LAZY_LOADED_SINGLE_FACETS.filter(facet => searchObject.filters.facets[facet.mapField + 'Ids']
          || topFacets.find(facetSchema => facetSchema.mapField === facet.mapField));
          return [
            new ResourceFacetsLoadCompleteAction({
              facets: facets as AllResourceFacets,
              facetsLocked,
            }),
            ...lazyLoadedFacets.map(facet => {
              return loadSingleLazyFacet({
                facetType: facet.mapField,
                searchObject: {
                  ...searchObject,
                  ...facet.searchObject
                },
                changedQuery: false,
              });
            }),
            new ExpandResourceFacetsAction(((action.payload.changedQuery && srpQueryResults.totalResults && !advancedMode)
              || (advancedMode && !hasSelectedFacets)) ? [0, 1, 2] : null),
          ];
        }),
        catchError((err) => of(new ResourceFacetsLoadCompleteAction({
          facets: null,
          facetsLocked: null,
        }))),
      );
    }),
  ));

  public loadLazyFacet: Observable<any> = createEffect(() => this.actions$.pipe(
    ofType(LOAD_LAZY_FACET),
    mergeMap((action: LazyFacetLoadAction) => {
      const searchObject = action.payload.searchObject;
      const facetsLocked: FacetsLocked = SearchFacetsEffects.getStoredLockedFacetsIds();
      return this.facetsService.getFacets(searchObject).pipe(
        map((facets) => {
          return new ResourceFacetsLoadCompleteAction({
            facets: facets as AllResourceFacets,
            facetsLocked,
            isLazyFacets: true,
            lazyFacetIds: searchObject.facetTypes,
          });
        }),
        catchError((err) => of(new ResourceFacetsLoadCompleteAction({facets: null, facetsLocked: null}))),
      );
    }),
  ));

  public loadSingleLazyFacet: Observable<any> = createEffect(() => this.actions$.pipe(
    ofType(loadSingleLazyFacet),
    mergeMap(({searchObject, facetType}) => {
      const facetsLocked: FacetsLocked = SearchFacetsEffects.getStoredLockedFacetsIds();
      const facet = this.LAZY_LOADED_SINGLE_FACETS.find(facet => facet.mapField === facetType);
      return this.facetsService.getSingleFacet({
        ...searchObject,
        facetMapField: facet.mapField,
        facetKey: facet.mapField
      }, searchObject).pipe(map((result: FacetResult<ResourceFacet>) => {
        if (facet.mapField == 'materialType') {
          result = SearchFacetsEffects.removeOAIPrefix(result);
        }
        const schemaWithData = this.facetsService.getModelForFacet(facet.mapField, result);
        return singleLazyFacetLoadCompleteAction({facet: result, facetKey: facet.key, schemaWithData, facetsLocked});
      }), catchError(() => of(singleLazyFacetLoadCompleteAction({
        facet: null,
        facetKey: facet.key,
        schemaWithData: null,
        facetsLocked: null
      }))));
    }),
  ));

  public routeWhenApplyResourceFacets = createEffect(() => this.actions$.pipe(
    ofType(APPLY_RESOURCE_FACETS),
    withLatestFrom(this.store.pipe(select(getV2Facets)),
        this.store.pipe(select(getLastSearchResultsUrl)),
        this.store.pipe(select(getCurrentSearchInput)),
        this.store.pipe(select(getAdvancedMode))),
    tap(([_, facets, lastQuery, currentSearchTerm, advancedMode]) => {
      const urlTree = this.router.parseUrl(lastQuery);
      const newQueryParams = facets.reduce((acc, facet) => {
        switch (facet.schemaWithData.schema.type) {
          case FacetType.Iterable: {
            const selectedFacets = facet.selected as Facet[];
            this.addLockedSelectedFacetIdToLocalStorage(selectedFacets);
            acc[facet.schemaWithData.schema.mapField + 'Ids'] = selectedFacets.length ? this.getSelectedFacetIds(selectedFacets) : null;
            return acc;
          }
          case FacetType.Date: {
            if (facet.selected) {
              const dateFacet = facet.selected as DateFacet;
              acc['dateFrom'] = dateFacet.dateFrom && dateFacet.selected ? dateFacet.dateFrom : null;
              acc['dateTo'] = dateFacet.dateTo && dateFacet.selected ? dateFacet.dateTo : null;
              this.addLockedSelectedFacetIdToLocalStorage([dateFacet]);
            }
            return acc;
          }
          case FacetType.CatalogDate: {
            if (facet.selected) {
              const catalogDateFacet = facet.selected as CatalogDateFacet;
              acc['catalogDate'] = catalogDateFacet.catalogDate && catalogDateFacet.selected ? catalogDateFacet.catalogDate : null;
              this.addLockedSelectedFacetIdToLocalStorage([catalogDateFacet]);
            }
            return acc;
          }
          case FacetType.Single: {
            const singleFacet = facet.selected as Facet;
            this.addLockedSelectedFacetIdToLocalStorage([singleFacet]);
            acc[facet.schemaWithData.schema.mapField] = singleFacet.selected ? true : null;
            return acc;
          }
        }
      }, {} as any);
      let queryParams = {
        ...urlTree.queryParams,
        ...newQueryParams,
        pageNum: 0,
      };
      if (advancedMode === true) {
        queryParams = {
          ...queryParams,
          query: !currentSearchTerm ? '*' : currentSearchTerm,
          mode: Advanced,
        };
      }
      this.router.navigate(['/search'], {queryParams});
    }),
  ), {dispatch: false});

  public removeFacetBubble: Observable<any> = createEffect(() => this.actions$.pipe(
    ofType(REMOVE_FACET_BUBBLE),
    pluck('payload'),
    switchMap(({facet}) => {
      const {facetKey, id} = facet;
      this.deleteLockedFacetIdFromLocalStorage(facetKey, id);
      return [
        new ApplyResourceFacetsAction(),
      ];
    }),
  ));

  public resetFacetBubbles: Observable<any> = createEffect(() => this.actions$.pipe(
    ofType(RESET_FACET_BUBBLES),
    switchMap(() => [
      new ApplyResourceFacetsAction(),
    ]),
  ));

  public handleFacetsLocked: Observable<any> = createEffect(() => this.actions$.pipe(
    ofType(HANDLE_FACETS_LOCK_IN_STORAGE),
    pluck('payload'),
    withLatestFrom(this.store.pipe(select(getAppliedFacetsArray))),
    map(([{facet}, appliedFacets]) => {
      const {facetKey, isResetLocked, id} = facet;
      if (isResetLocked) {
        this.deleteLockedFacetIdFromLocalStorage(facetKey, id);
      } else {
        const isApplied = appliedFacets.some((facet) => facet.id === id);
        if (isApplied) {
          this.addLockedFacetIdToLocalStorage(facetKey, id);
        }
      }

      return new ToggleFacetBubbleLockAction({facet});
    }),
  ));

  public clearFacetsLocked: Observable<any> = createEffect(() => this.actions$.pipe(
    ofType(CLEAR_FACETS_LOCK_IN_STORAGE),
    map(() => {
        localStorage.setItem(facetKeyStorage, '{}');
        return new ClearResourceFacetsAction();
    }),
  ));

  public handleRapidoModeInUrl$ = createEffect(() => this.actions$.pipe(
    ofType<RouterNavigationAction>(ROUTER_NAVIGATION),
    withLatestFrom(this.store.select(getRapidoMode)),
    filter(([action, rapidoMode]) => {
      const url = action.payload.event.url;
      const queryParams = action.payload.routerState.root.queryParams;
      return this.isValidSearchUrl(url) && rapidoMode && !queryParams['rapido'];
    }),
    map(([action]) => {
      const url = action.payload.event.url;
      const queryParams = {...action.payload.routerState.root.queryParams, rapido: 'true'};
      this.router.navigate([], { queryParams, queryParamsHandling: 'merge' });
    }),
  ), { dispatch: false });

  constructor(
    private readonly actions$: Actions<SearchActions | FacetActions>,
    private readonly facetsService: FacetsService,
    private readonly searchMapperService: SearchMapperService,
    private readonly store: Store<State>,
    private readonly router: Router,
    private readonly facetSchemaService: FacetsSchemaService,
    private readonly featureToggleService: FeatureToggleService
  ) {
      this.LAZY_LOADED_SINGLE_FACETS = [...this.LAZY_LOADED_SINGLE_FACETS, {
        mapField: 'location',
        key: 'LOCATIONS',
        searchObject: DEFAULT_LAZY_LOADED_FACET_SEARCH_OBJECT['location']
      }];

    if (this.featureToggleService.getToggles()['VE-4775_2024-07-31_format_facet_search']) {
      this.LAZY_LOADED_SINGLE_FACETS = [...this.LAZY_LOADED_SINGLE_FACETS, {
        mapField: 'materialType',
        key: 'FORMATS',
        searchObject: DEFAULT_LAZY_LOADED_FACET_SEARCH_OBJECT['materialType']
      }];
    }
  }

  private static getStoredLockedFacetsIds(): FacetsLocked {
    return localStorage.getItem(facetKeyStorage) ? JSON.parse(localStorage.getItem(facetKeyStorage)) : {};
  }

  private static removeOAIPrefix(result: FacetResult<ResourceFacet>) {
    const mappedResult = {...result, data: result.data.map(facet => {
      const [,,match] = facet.label.match(OAI_MATERIAL_TYPE_REGEX) ?? [];
      if (match) {
        return {...facet, label: match};
      }
      return facet;
    })};
    return mappedResult;
  }

  private static getStoredLockedFacetsIdsByKey(storedFacetsIds: FacetsLocked, key: string): string[] {
    return storedFacetsIds.hasOwnProperty(key) ? storedFacetsIds[key] : [];
  }

  private getSelectedFacetIds(data: Facet[]) {
    return data.filter((el) => el.selected)
      .map((el) => encodeURIComponent(el.id))
      .join(',');
  }

  private isValidSearchUrl(url: string): boolean {
    return /^\/search\?.*(&|)(bool|)query=/i.test(url) || /^\/search\?.*(&|)metadata=/i.test(url);
  }

  private validateAndNavigate(queryParams: any) {
    let isValid = true;
    if (queryParams.pageSize && +queryParams.pageSize > MAX_PAGE_SIZE) {
      queryParams.pageSize = MAX_PAGE_SIZE;
      isValid = false;
    }
    if (queryParams.pageNum && +queryParams.pageNum > MAX_PAGE_NUMBER) {
      queryParams.pageNum = MAX_PAGE_NUMBER;
      isValid = false;
    }
    if (!isValid) {
      this.router.navigate(['/search'], {queryParams});
    }
  }

  private addLockedFacetIdToLocalStorage(key: string, id: string) {
    const storedFacetsIds = SearchFacetsEffects.getStoredLockedFacetsIds();
    const storedFacetsIdsByKey = SearchFacetsEffects.getStoredLockedFacetsIdsByKey(storedFacetsIds, key);
    const storedFacetsIdsAdded = [...storedFacetsIdsByKey, id];
    storedFacetsIds[key] = [...new Set(storedFacetsIdsAdded)];
    localStorage.setItem(facetKeyStorage, JSON.stringify(storedFacetsIds));
  }

  private deleteLockedFacetIdFromLocalStorage(key: string, id: string) {
    const storedFacetsIds = SearchFacetsEffects.getStoredLockedFacetsIds();
    const storedFacetsIdsByKey = SearchFacetsEffects.getStoredLockedFacetsIdsByKey(storedFacetsIds, key);
    if (storedFacetsIdsByKey.length) {
      const storedFacetsIdsRemoved = storedFacetsIdsByKey.filter((item: string) => item !== id);
      storedFacetsIds[key] = [...storedFacetsIdsRemoved];
      localStorage.setItem(facetKeyStorage, JSON.stringify(storedFacetsIds));
    }
  }

  private addLockedSelectedFacetIdToLocalStorage(facets: Facet[] | DateFacet[] | CatalogDateFacet[]) {
    (facets as (Facet | DateFacet | CatalogDateFacet)[]).forEach((facet: Facet | DateFacet | CatalogDateFacet) => {
      if (facet.isResetLocked) {
        this.addLockedFacetIdToLocalStorage(facet.facetKey, facet.id);
      }
    });
  }

  private lockAdvancedSearchFacets(filters: FacetURLFilters, facetSchemaList: FacetSchemaWithData[]): boolean {
    const lockedFacets: FacetsLocked = {};
    let hasLockedFacets = false;
    facetSchemaList.forEach((schemaWithData) => {
      const lockedFacet = getProcessor(schemaWithData.schema.type).getAppliedFiltersForLock(schemaWithData, filters);
      if (lockedFacet) {
        lockedFacets[schemaWithData.schema.key] = lockedFacet;
        hasLockedFacets = true;
      }
    });
    localStorage.setItem(facetKeyStorage, JSON.stringify(lockedFacets));
    return hasLockedFacets;
  }
}
